import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomInput from "global/components/CustomInput/CustomInput";
import {
  getConfigurationData,
  handleEnableConfigUpdateBtn,
  handleUpdate,
} from "screens/MySignRequests/MySignRequestHelper";
import {
  getDefaultDateFormat,
  updateDefaultDateFormatConfig,
} from "screens/MySignRequests/MySignRequestService";
import {
  handleErrorMessage,
  javaToMomentFormat,
  momentToJavaFormat,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import { selectEmail } from "redux/authSlice";
import { useAppSelector } from "hooks/use-app-select";
import { useAppDispatch } from "redux/hooks";
import {
  selectDefaultDateFormat,
  updateDefaultDateFormat,
} from "redux/preferencesSlice";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import strings from "global/constants/StringConstants";
import { useFeatureState, FeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";
import {
  AttachmentConfig,
  ATTACHMENT_MAX_FILES_LIMIT,
  ATTACHMENT_MAX_FILE_SIZE_LIMIT_MB,
  ATTACHMENT_TYPE_OPTIONS,
  DEFAULT_ATTACHMENT_CONFIG,
  getAttachmentConfig,
  invalidateAccountAttachmentConfig,
  updateAttachmentConfig,
} from "helpers/attachmentConfig";
import SettingsLayout from "../components/SettingsLayout";
import documentSettingsStyles from "./DocumentSettings.styles";
import documentSettingsTokens from "./DocumentSettings.tokens";
import { SelectChevronIcon } from "./components/DocumentSettingsIcons";

const styles = documentSettingsStyles;

/**
 * `Select`'s `IconComponent` — MUI hands it the `.MuiSelect-icon` class, which
 * `DocumentSettings.styles.ts` sizes and positions.
 */
const DateFormatSelectIcon = ({ className }: { className?: string }) => (
  <SelectChevronIcon className={className} />
);

const DATE_FORMAT_OPTIONS = [
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD MMM YYYY",
  "MMM DD, YYYY",
];

const DocumentSettings = () => {
  useTitle(title.DOCUMENT_SETTINGS);
  const userEmail: string = useAppSelector(selectEmail);
  const appDispatch = useAppDispatch();
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);

  const reminderState = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const autoExpirationState = useFeatureState(
    FeatureKeys.ENVELOPE_AUTO_EXPIRATION,
  );
  const isReminderAvailable =
    reminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isAutoExpirationAvailable =
    autoExpirationState.status === strings.FEATURE_STATUS_AVAILABLE;

  const getFeatureTooltip = (state: FeatureState): string => {
    if (state.status === strings.FEATURE_STATUS_NOT_IN_PLAN)
      return strings.FEATURE_GATE_UPSELL_TOOLTIP;
    if (state.status === strings.FEATURE_STATUS_ADMIN_DISABLED)
      return (
        state.disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP
      );
    return "";
  };

  const [loading, setLoading] = useState(false);

  const [configurationFields, setConfigurationFields] = useState<any>({
    autoReminder: false,
    daysBeforeFirstReminder: 0,
    daysBetweenReminders: 0,
    expirationDays: 0,
    allowSignatureDelegation: false,
    delegateName: "",
    delegateEmail: "",
    delegateStartDate: "",
    delegateEndDate: "",
  });

  const [previousValues, setPreviousValues] = useState<any>({
    autoReminder: false,
    daysBeforeFirstReminder: 0,
    daysBetweenReminders: 0,
    expirationDays: 0,
    allowSignatureDelegation: false,
    delegateName: "",
    delegateEmail: "",
    delegateStartDate: "",
    delegateEndDate: "",
  });

  const [updateButtonEnabled, setUpdateButtonEnabled] = useState({
    btn1: false,
    btn2: false,
    btn3: false,
  });

  const [errors, setErrors] = useState<any>({
    daysBeforeFirstReminder: "",
    daysBetweenReminders: "",
  });

  const [autoExpErrors, setAutoExpErrors] = useState<any>({
    expirationDays: "",
  });
  const [autoDelegateErrors, setAutoDelegateErrors] = useState<any>({
    delegationEmail: "",
    delegationName: "",
    delegationDateRange: "",
  });

  useEffect(() => {
    if (userEmail) {
      getConfigurationData(
        setLoading,
        setConfigurationFields,
        setPreviousValues,
        userEmail,
      );
    }
  }, [userEmail]);

  const [localDateFormat, setLocalDateFormat] =
    useState<string>(defaultDateFormat);
  const [dateFormatUpdating, setDateFormatUpdating] = useState<boolean>(false);
  const isDateFormatDirty = localDateFormat !== defaultDateFormat;

  useEffect(() => {
    const fetchDateFormat = async () => {
      try {
        const response = await getDefaultDateFormat();
        const raw =
          typeof response === "string"
            ? response
            : (response?.value ?? response?.data?.value ?? response?.dateFormat);
        if (raw && typeof raw === "string") {
          const momentFmt = javaToMomentFormat(raw);
          appDispatch(updateDefaultDateFormat(momentFmt));
          setLocalDateFormat(momentFmt);
        }
      } catch (err) {
        // Silent fail
      }
    };
    fetchDateFormat();
  }, []);

  useEffect(() => {
    setLocalDateFormat(defaultDateFormat);
  }, [defaultDateFormat]);

  const handleDateFormatUpdate = async () => {
    if (!isDateFormatDirty) return;
    setDateFormatUpdating(true);
    setLoading(true);
    try {
      await updateDefaultDateFormatConfig(momentToJavaFormat(localDateFormat));
      appDispatch(updateDefaultDateFormat(localDateFormat));
    } catch (err: any) {
      handleErrorMessage(err);
    } finally {
      setDateFormatUpdating(false);
      setLoading(false);
    }
  };

  const [attachmentConfig, setAttachmentConfig] = useState<AttachmentConfig>(
    DEFAULT_ATTACHMENT_CONFIG,
  );
  const [savedAttachmentConfig, setSavedAttachmentConfig] =
    useState<AttachmentConfig>(DEFAULT_ATTACHMENT_CONFIG);
  const [attachmentUpdating, setAttachmentUpdating] = useState<boolean>(false);

  useEffect(() => {
    const fetchAttachmentConfig = async () => {
      try {
        const config = await getAttachmentConfig();
        setAttachmentConfig(config);
        setSavedAttachmentConfig(config);
      } catch (err: any) {
        handleErrorMessage(err);
      }
    };
    fetchAttachmentConfig();
  }, []);

  const attachmentErrors = {
    allowedTypes:
      attachmentConfig.allowedTypes.length === 0
        ? "Select at least one file type."
        : "",
    maxFileSizeMb:
      Number(attachmentConfig.maxFileSizeMb) <= 0 ||
      Number(attachmentConfig.maxFileSizeMb) > ATTACHMENT_MAX_FILE_SIZE_LIMIT_MB
        ? `Enter a size between 1 and ${ATTACHMENT_MAX_FILE_SIZE_LIMIT_MB} MB.`
        : "",
    maxFilesPerSession:
      Number(attachmentConfig.maxFilesPerSession) <= 0 ||
      Number(attachmentConfig.maxFilesPerSession) > ATTACHMENT_MAX_FILES_LIMIT
        ? `Enter a number between 1 and ${ATTACHMENT_MAX_FILES_LIMIT}.`
        : "",
  };

  const hasAttachmentErrors = Object.values(attachmentErrors).some(Boolean);

  const isAttachmentDirty =
    attachmentConfig.allowedTypes.join(",") !==
      savedAttachmentConfig.allowedTypes.join(",") ||
    Number(attachmentConfig.maxFileSizeMb) !==
      Number(savedAttachmentConfig.maxFileSizeMb) ||
    Number(attachmentConfig.maxFilesPerSession) !==
      Number(savedAttachmentConfig.maxFilesPerSession);

  const handleAttachmentNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;
    setAttachmentConfig((prev) => ({
      ...prev,
      [name]: value.replace(/[^0-9]/g, ""),
    }));
  };

  const handleAttachmentUpdate = async () => {
    if (!isAttachmentDirty || hasAttachmentErrors) return;
    setAttachmentUpdating(true);
    setLoading(true);
    try {
      const saved = await updateAttachmentConfig({
        allowedTypes: attachmentConfig.allowedTypes,
        maxFileSizeMb: Number(attachmentConfig.maxFileSizeMb),
        maxFilesPerSession: Number(attachmentConfig.maxFilesPerSession),
      });
      setAttachmentConfig(saved);
      setSavedAttachmentConfig(saved);
      invalidateAccountAttachmentConfig();
      openSuccessNotification(notifiers.SIGNER_ATTACHMENTS_UPDATED);
    } catch (err: any) {
      handleErrorMessage(err);
    } finally {
      setAttachmentUpdating(false);
      setLoading(false);
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    const isString = isNaN(Number(value));
    const valueByType = isString ? value : value.replace(/[^0-9]/g, "");
    setConfigurationFields((prev: any) => ({ ...prev, [name]: valueByType }));
    handleEnableConfigUpdateBtn(
      name,
      valueByType,
      setUpdateButtonEnabled,
      updateButtonEnabled,
      previousValues,
    );
  };

  const handleToggleReminder = () => {
    const checked = !configurationFields.autoReminder;
    setConfigurationFields((prev: any) => ({ ...prev, autoReminder: checked }));
    handleEnableConfigUpdateBtn(
      "autoReminder",
      checked,
      setUpdateButtonEnabled,
      updateButtonEnabled,
      previousValues,
    );
  };

  const reminderTooltip = getFeatureTooltip(reminderState);
  const autoExpirationTooltip = getFeatureTooltip(autoExpirationState);
  const effectiveAutoReminder =
    isReminderAvailable && configurationFields.autoReminder;

  const isFirstReminderInvalid = configurationFields.daysBeforeFirstReminder <= 0;
  const isRepeatReminderInvalid = configurationFields.daysBetweenReminders <= 0;
  const isExpirationInvalid =
    configurationFields.expirationDays > 180 ||
    configurationFields.expirationDays <= 0;

  /** Inline "<label> [ n ] days" row shared by the reminder and expiry cards. */
  const getNumberRow = (
    label: string,
    name: string,
    value: string | number,
    hasError: boolean,
    disabled: boolean,
    suffix: string = "days",
    onChange: (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => void = handleOnChange,
  ) => (
    <Box sx={styles.numberRow}>
      <InputLabel htmlFor={name} sx={styles.numberRowLabel}>
        {label}
      </InputLabel>
      <CustomInput
        required
        widthNotRequired
        id={name}
        name={name}
        type="text"
        textAlign="center"
        disabled={disabled}
        onChange={onChange}
        value={value}
        propsToInputElement={{
          minLength: 1,
          maxLength: 3,
          "aria-describedby": hasError ? `${name}-error` : undefined,
          "aria-invalid": hasError || undefined,
        }}
        sx={styles.numberField(hasError)}
      />
      <Typography component="span" sx={styles.numberRowSuffix}>
        {suffix}
      </Typography>
    </Box>
  );

  const getReminderCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Automated Email Reminder
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            Nudge signers to complete the signing process and expire untouched
            requests.
          </Typography>
        </Box>
        <Tooltip
          title={reminderTooltip}
          disableHoverListener={isReminderAvailable}
          arrow
          describeChild
        >
          <Box component="span" sx={styles.toggleWrapper}>
            <ButtonBase
              disableRipple
              role="switch"
              aria-checked={effectiveAutoReminder}
              aria-label="Automated Email Reminder"
              disabled={!isReminderAvailable}
              onClick={handleToggleReminder}
              sx={styles.toggleTrack(effectiveAutoReminder)}
            >
              <Box sx={styles.toggleKnob(effectiveAutoReminder)} />
            </ButtonBase>
            {!isReminderAvailable && <LockOutlinedIcon sx={styles.toggleLock} />}
          </Box>
        </Tooltip>
      </Box>

      {!isReminderAvailable && (
        <Box sx={{ mb: 2 }}>
          <FeatureBanner
            state={reminderState}
            featureName="Automated reminders"
            variant="compact"
          />
        </Box>
      )}

      <Box sx={styles.numberRowStack(effectiveAutoReminder)}>
        {getNumberRow(
          "Set first reminder in",
          "daysBeforeFirstReminder",
          configurationFields.daysBeforeFirstReminder,
          isFirstReminderInvalid && !!errors.daysBeforeFirstReminder,
          !effectiveAutoReminder,
        )}
        {isFirstReminderInvalid && errors.daysBeforeFirstReminder && (
          <FormHelperText
            error
            role="alert"
            id="daysBeforeFirstReminder-error"
            sx={styles.fieldError}
          >
            {errors.daysBeforeFirstReminder}
          </FormHelperText>
        )}

        {getNumberRow(
          "Repeat reminder after",
          "daysBetweenReminders",
          configurationFields.daysBetweenReminders,
          isRepeatReminderInvalid && !!errors.daysBetweenReminders,
          !effectiveAutoReminder,
        )}
        {isRepeatReminderInvalid && errors.daysBetweenReminders && (
          <FormHelperText
            error
            role="alert"
            id="daysBetweenReminders-error"
            sx={styles.fieldError}
          >
            {errors.daysBetweenReminders}
          </FormHelperText>
        )}
      </Box>

      <Box sx={styles.buttonRow(effectiveAutoReminder)}>
        <Tooltip
          title={reminderTooltip}
          disableHoverListener={isReminderAvailable}
          arrow
          describeChild
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Button
              id="doc_settings_reminder_update_btn"
              variant="contained"
              disabled={!updateButtonEnabled.btn1 || !isReminderAvailable}
              endIcon={!isReminderAvailable ? <LockOutlinedIcon /> : undefined}
              onClick={() =>
                handleUpdate(
                  "autoEmailReminder",
                  setLoading,
                  setPreviousValues,
                  errors,
                  setErrors,
                  autoExpErrors,
                  setAutoExpErrors,
                  configurationFields,
                  setUpdateButtonEnabled,
                  updateButtonEnabled,
                  autoDelegateErrors,
                  setAutoDelegateErrors,
                  userEmail,
                )
              }
              sx={styles.primaryButton}
            >
              Update
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const getAutoExpirationCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Auto Expiration
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            Applied to all new requests and templates. Unsigned requests expire
            after this period.
          </Typography>
        </Box>
        {!isAutoExpirationAvailable && (
          <Tooltip title={autoExpirationTooltip} arrow describeChild>
            <Box component="span" sx={styles.toggleWrapper}>
              <LockOutlinedIcon sx={styles.toggleLock} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {!isAutoExpirationAvailable && (
        <Box sx={{ mb: 2 }}>
          <FeatureBanner
            state={autoExpirationState}
            featureName="Auto expiration"
            variant="compact"
          />
        </Box>
      )}

      <Box sx={styles.numberRowStack(isAutoExpirationAvailable)}>
        <Tooltip
          title={autoExpirationTooltip}
          disableHoverListener={isAutoExpirationAvailable}
          arrow
          describeChild
        >
          <Box component="span" sx={{ display: "block" }}>
            {getNumberRow(
              "Sent request expires in",
              "expirationDays",
              configurationFields.expirationDays,
              isExpirationInvalid && !!autoExpErrors.expirationDays,
              !isAutoExpirationAvailable,
            )}
          </Box>
        </Tooltip>
        {isExpirationInvalid && autoExpErrors.expirationDays && (
          <FormHelperText
            error
            role="alert"
            id="expirationDays-error"
            sx={styles.fieldError}
          >
            {autoExpErrors.expirationDays}
          </FormHelperText>
        )}
      </Box>

      <Box sx={styles.buttonRow(isAutoExpirationAvailable)}>
        <Tooltip
          title={autoExpirationTooltip}
          disableHoverListener={isAutoExpirationAvailable}
          arrow
          describeChild
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Button
              id="doc_settings_expiration_update_btn"
              variant="contained"
              disabled={!updateButtonEnabled.btn2 || !isAutoExpirationAvailable}
              endIcon={
                !isAutoExpirationAvailable ? <LockOutlinedIcon /> : undefined
              }
              onClick={() =>
                handleUpdate(
                  "autoExpirations",
                  setLoading,
                  setPreviousValues,
                  errors,
                  setErrors,
                  autoExpErrors,
                  setAutoExpErrors,
                  configurationFields,
                  setUpdateButtonEnabled,
                  updateButtonEnabled,
                  autoDelegateErrors,
                  setAutoDelegateErrors,
                  userEmail,
                )
              }
              sx={styles.primaryButton}
            >
              Update
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const getDateFormatCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Default Date Format
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            Used whenever a Date or Date Signed field is added. Each field can
            still be changed individually.
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.selectGroup}>
        <InputLabel
          id="defaultDateFormatLabel"
          htmlFor="defaultDateFormat"
          sx={styles.selectLabel}
        >
          Date format
        </InputLabel>
        <Select
          id="defaultDateFormat"
          labelId="defaultDateFormatLabel"
          value={localDateFormat}
          onChange={(event) => setLocalDateFormat(event.target.value as string)}
          IconComponent={DateFormatSelectIcon}
          MenuProps={{
            PaperProps: { sx: styles.selectMenu },
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
          }}
          sx={styles.select}
        >
          {DATE_FORMAT_OPTIONS.map((format) => (
            <MenuItem key={format} value={format} sx={styles.selectOption}>
              {format}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={styles.buttonRow(true)}>
        <Button
          id="doc_settings_date_format_update_btn"
          variant="contained"
          disabled={!isDateFormatDirty || dateFormatUpdating}
          onClick={handleDateFormatUpdate}
          sx={styles.primaryButton}
        >
          Update
        </Button>
      </Box>
    </Box>
  );

  const getSignerAttachmentsCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Signer Attachments
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            Applies to every Attachment field signers are asked to complete.
            Limits count per signer, for each signing session.
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.selectGroup}>
        <InputLabel
          id="attachmentAllowedTypesLabel"
          htmlFor="attachmentAllowedTypes"
          sx={styles.selectLabel}
        >
          Allowed file types
        </InputLabel>
        <Select
          multiple
          id="attachmentAllowedTypes"
          labelId="attachmentAllowedTypesLabel"
          value={attachmentConfig.allowedTypes}
          onChange={(event) =>
            setAttachmentConfig((prev) => ({
              ...prev,
              allowedTypes: event.target.value as string[],
            }))
          }
          displayEmpty
          renderValue={(selected) => (
            // The value row is `display: flex`, so a long list would be clipped
            // mid-letter rather than ellipsised — with every type selected the
            // labels run past the box. The span puts the text back in a block
            // that can truncate.
            <Box component="span" sx={styles.selectValueText}>
              {(selected as string[]).length === 0
                ? "Select file types"
                : ATTACHMENT_TYPE_OPTIONS.filter((option) =>
                    (selected as string[]).includes(option.value),
                  )
                    .map((option) => option.label)
                    .join(", ")}
            </Box>
          )}
          IconComponent={DateFormatSelectIcon}
          MenuProps={{
            PaperProps: { sx: styles.selectMenu },
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            transformOrigin: { vertical: "top", horizontal: "left" },
          }}
          inputProps={{
            "aria-describedby": attachmentErrors.allowedTypes
              ? "attachmentAllowedTypes-error"
              : undefined,
            "aria-invalid": !!attachmentErrors.allowedTypes || undefined,
          }}
          sx={
            attachmentErrors.allowedTypes ? styles.selectError : styles.select
          }
        >
          {ATTACHMENT_TYPE_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={styles.selectOptionMultiple}
            >
              <Checkbox
                size="small"
                disableRipple
                tabIndex={-1}
                checked={attachmentConfig.allowedTypes.includes(option.value)}
              />
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {attachmentErrors.allowedTypes && (
          <FormHelperText
            error
            role="alert"
            id="attachmentAllowedTypes-error"
            sx={styles.fieldError}
          >
            {attachmentErrors.allowedTypes}
          </FormHelperText>
        )}
      </Box>

      <Box sx={styles.numberRowStack(true)}>
        {getNumberRow(
          "Maximum file size",
          "maxFileSizeMb",
          attachmentConfig.maxFileSizeMb,
          !!attachmentErrors.maxFileSizeMb,
          false,
          "MB",
          handleAttachmentNumberChange,
        )}
        {attachmentErrors.maxFileSizeMb && (
          <FormHelperText
            error
            role="alert"
            id="maxFileSizeMb-error"
            sx={styles.fieldError}
          >
            {attachmentErrors.maxFileSizeMb}
          </FormHelperText>
        )}

        {getNumberRow(
          "Maximum attachments",
          "maxFilesPerSession",
          attachmentConfig.maxFilesPerSession,
          !!attachmentErrors.maxFilesPerSession,
          false,
          "files",
          handleAttachmentNumberChange,
        )}
        {attachmentErrors.maxFilesPerSession && (
          <FormHelperText
            error
            role="alert"
            id="maxFilesPerSession-error"
            sx={styles.fieldError}
          >
            {attachmentErrors.maxFilesPerSession}
          </FormHelperText>
        )}
      </Box>

      <Box sx={styles.buttonRow(true)}>
        <Button
          id="doc_settings_attachments_update_btn"
          variant="contained"
          disabled={
            !isAttachmentDirty || hasAttachmentErrors || attachmentUpdating
          }
          onClick={handleAttachmentUpdate}
          sx={styles.primaryButton}
        >
          Update
        </Button>
      </Box>
    </Box>
  );

  const getCardSkeleton = (rowCount: number) => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.skeletonHeaderText}>
          <Skeleton
            variant="text"
            width={documentSettingsTokens.skeleton.titleWidth}
            height={documentSettingsTokens.skeleton.titleHeight}
          />
          <Skeleton
            variant="text"
            width="100%"
            height={documentSettingsTokens.skeleton.descriptionHeight}
          />
          <Skeleton
            variant="text"
            width={documentSettingsTokens.skeleton.descriptionWidth}
            height={documentSettingsTokens.skeleton.descriptionHeight}
          />
        </Box>
      </Box>
      <Box sx={styles.numberRowStack(true)}>
        {Array.from({ length: rowCount }, (_, index) => (
          <Box key={index} sx={styles.numberRow}>
            <Skeleton
              variant="text"
              width={documentSettingsTokens.skeleton.rowLabelWidth}
              height={documentSettingsTokens.skeleton.rowLabelHeight}
            />
            <Skeleton
              variant="rounded"
              width={documentSettingsTokens.numberField.width}
              height={documentSettingsTokens.numberField.height}
              sx={{ borderRadius: documentSettingsTokens.numberField.radius }}
            />
          </Box>
        ))}
      </Box>
      <Box sx={styles.buttonRow(true)}>
        <Skeleton
          variant="rounded"
          width={documentSettingsTokens.skeleton.buttonWidth}
          height={documentSettingsTokens.primaryButton.height}
          sx={{ borderRadius: documentSettingsTokens.primaryButton.radius }}
        />
      </Box>
    </Box>
  );

  return (
    <SettingsLayout activeTab="Document Settings" title="Document Settings">
      <Box sx={styles.grid} aria-busy={loading}>
        {loading ? (
          <>
            {getCardSkeleton(2)}
            {getCardSkeleton(1)}
            {getCardSkeleton(1)}
            {getCardSkeleton(2)}
          </>
        ) : (
          <>
            {getReminderCard()}
            {getAutoExpirationCard()}
            {getDateFormatCard()}
            {getSignerAttachmentsCard()}
          </>
        )}
      </Box>
    </SettingsLayout>
  );
};

export default DocumentSettings;
